export const normalizeMileageRateMonths = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((entry) => parseInt(entry, 10))
      .filter((entry) => Number.isInteger(entry) && entry >= 1 && entry <= 12);
    return [...new Set(normalized)].sort((a, b) => a - b);
  }

  const normalized = String(value)
    .split(",")
    .map((entry) => parseInt(entry.trim(), 10))
    .filter((entry) => Number.isInteger(entry) && entry >= 1 && entry <= 12);

  return [...new Set(normalized)].sort((a, b) => a - b);
};

export const isMonthCoveredByRate = (rate, month) => {
  if (!rate) return false;
  const months = normalizeMileageRateMonths(rate?.months);
  if (!months || months.length === 0) return true;
  return months.includes(Number(month));
};

// Resolve a year/month from any supported input: dayjs instance, native Date,
// date string, or bare year number.
// dayjs is read via .year()/.month() (calendar values), so UTC-based dates
// like "2026-06-15T00:00:00Z" do NOT shift a month when converted to a local
// timezone Date (e.g. June 1 -> May 31 in negative-offset zones).
const getDateYearMonth = (dateValue) => {
  if (dateValue == null) return { year: NaN, month: NaN };

  // dayjs instance
  if (
    typeof dateValue === "object" &&
    typeof dateValue.year === "function" &&
    typeof dateValue.month === "function"
  ) {
    return {
      year: Number(dateValue.year()),
      month: Number(dateValue.month()) + 1,
    };
  }

  // native Date
  if (
    typeof dateValue === "object" &&
    typeof dateValue.getFullYear === "function"
  ) {
    return {
      year: dateValue.getFullYear(),
      month: dateValue.getMonth() + 1,
    };
  }

  // bare year number (legacy usage) — treated as January
  if (typeof dateValue === "number") {
    return { year: dateValue, month: 1 };
  }

  // date string (e.g. "2026-06-15")
  if (typeof dateValue === "string") {
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return { year: parsed.getFullYear(), month: parsed.getMonth() + 1 };
    }
  }

  return { year: NaN, month: NaN };
};

export const getMileageRateForDate = (
  mileageRates = [],
  dateValue,
  fallbackRate = 0.7,
) => {
  if (!Array.isArray(mileageRates) || mileageRates.length === 0) {
    return fallbackRate;
  }

  const { year: targetYear, month: targetMonth } = getDateYearMonth(dateValue);

  if (!targetYear || !targetMonth) {
    return fallbackRate;
  }

  const sameYearRates = mileageRates.filter(
    (rate) => Number(rate.year) === targetYear,
  );

  for (let month = targetMonth; month >= 1; month -= 1) {
    const matchingRate = sameYearRates.find((rate) =>
      isMonthCoveredByRate(rate, month),
    );

    if (matchingRate) {
      return parseFloat(matchingRate.rate);
    }
  }

  const previousYearRates = mileageRates
    .filter((rate) => Number(rate.year) < targetYear)
    .sort((a, b) => Number(b.year) - Number(a.year));

  if (previousYearRates.length > 0) {
    // Deterministic tie-break (2026-10-01 fix): when the latest previous
    // year has MULTIPLE rate rows (e.g. a mid-year rate update — 2026 has
    // rows for months 1-6 and 7-12), take the rate with the LATEST month
    // coverage — the most recent rate in effect — instead of whichever row
    // happened to come first in the array. The server and client array
    // orders differed, so the same future date resolved to different
    // expected rates (0.725 vs 0.760 for 2027 dates), which made the
    // mileage-rate-mismatch rule flag correctly priced assignments.
    const latestYear = Number(previousYearRates[0].year);
    const latestYearRates = previousYearRates.filter(
      (rate) => Number(rate.year) === latestYear,
    );
    const lastCoveredMonth = (rate) => {
      const months = normalizeMileageRateMonths(rate?.months);
      if (!months || months.length === 0) return 12;
      return Math.max(...months);
    };
    const mostRecent = latestYearRates.reduce(
      (best, candidate) =>
        !best || lastCoveredMonth(candidate) >= lastCoveredMonth(best)
          ? candidate
          : best,
      null,
    );
    return parseFloat(mostRecent.rate);
  }

  return fallbackRate;
};

export const formatMileageRateCoverageLabel = (rate) => {
  const months = normalizeMileageRateMonths(rate?.months);
  if (!months || months.length === 0 || months.length === 12) {
    return "All months";
  }

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return months.map((month) => monthNames[month - 1]).join(", ");
};
